import { useMemo, useState } from "react";
import useSWR from "swr";
import { X } from "lucide-react";
import { MENTION_TEXT_FIELDS, listMentionSlugs, removeMentionToken, referenceBindings, type MentionTextField } from "@/lib/mentions";
import { REFERENCE_FIELDS, normalizeReference, type RecreateReference } from "@/lib/recreate";
import {
  productionAssetsPath,
  swrFetcher,
  type AssetType,
  type ProductionAssetInfo,
} from "@/lib/api";
import { ReferencePreviewButton } from "@/components/studio/ReferencePreviewButton";
import { ReferenceLightbox, type ReferencePreviewTarget } from "./ReferenceLightbox";

interface MentionChipEntry {
  field: MentionTextField;
  slug: string;
}

const MENTION_FIELD_LABELS: Record<MentionTextField, string> = {
  prompt: "Prompt",
  subject: "Subject",
  action: "Action",
  performance: "Performance",
  dialogue: "Dialogue",
  environment: "Environment",
  camera: "Camera",
  lighting: "Lighting",
};

const CARD = "relative inline-flex max-w-full flex-col gap-1 rounded-xl border border-studio-border-light bg-studio-panel p-1.5 text-[11px]";
const REMOVE = "absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full border border-studio-border bg-studio-panel text-studio-ink hover:bg-studio-card";

function collectMentions(
  params: Record<string, unknown>,
  elementBySlug: Map<string, ProductionAssetInfo>
): MentionChipEntry[] {
  const entries: MentionChipEntry[] = [];
  for (const field of MENTION_TEXT_FIELDS) {
    const value = params[field];
    if (typeof value !== "string" || !value) continue;
    for (const slug of listMentionSlugs(value)) {
      if (elementBySlug.has(slug)) entries.push({ field, slug });
    }
  }
  return entries;
}

interface ReferenceChipEntry {
  field: string;
  index: number | null;
  reference: RecreateReference;
}

function collectReferences(params: Record<string, unknown>): ReferenceChipEntry[] {
  const entries: ReferenceChipEntry[] = [];
  for (const [field, fallbackType] of Object.entries(REFERENCE_FIELDS)) {
    const raw = params[field];
    if (Array.isArray(raw)) {
      raw.forEach((item, index) => {
        const reference = normalizeReference(item, fallbackType);
        if (reference) entries.push({ field, index, reference });
      });
    } else if (raw !== undefined) {
      const reference = normalizeReference(raw, fallbackType);
      if (reference) entries.push({ field, index: null, reference });
    }
  }
  return entries;
}

function referenceFieldLabel(field: string): string {
  switch (field) {
    case "image": return "Start frame";
    case "end_image": return "End frame";
    case "source_video": return "Source video";
    case "reference_images": return "Reference image";
    case "reference_videos": return "Reference video";
    case "reference_audios": return "Reference audio";
    default: return field;
  }
}

function referenceType(reference: RecreateReference): AssetType {
  return reference.type === "video" ? "video" : reference.type === "audio" ? "audio" : "image";
}

interface Props {
  params: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}

export function ComposerChips({ params, onChange }: Props) {
  const [preview, setPreview] = useState<ReferencePreviewTarget | null>(null);
  const { data } = useSWR<ProductionAssetInfo[]>(productionAssetsPath(undefined, undefined, false), swrFetcher);
  const elements = useMemo(() => (data ?? []).filter((asset) => !asset.archived), [data]);
  const elementBySlug = useMemo(() => {
    const index = new Map<string, ProductionAssetInfo>();
    // Legacy text may resolve only to one accessible Element. Bound aliases
    // always use the selected ID, including an admin's duplicate-name choices.
    for (const element of elements) {
      if (elements.filter((candidate) => candidate.slug === element.slug).length === 1) index.set(element.slug, element);
    }
    for (const binding of referenceBindings(params)) {
      if (binding.kind !== "element") continue;
      const element = elements.find((candidate) => candidate.id === binding.id);
      if (element) index.set(binding.token.slice(1), element);
    }
    return index;
  }, [elements, params]);

  const mentionEntries = useMemo(() => collectMentions(params, elementBySlug), [params, elementBySlug]);
  const references = useMemo(() => collectReferences(params), [params]);

  if (mentionEntries.length === 0 && references.length === 0 && !preview) return null;

  const removeMention = (entry: MentionChipEntry) => {
    const current = params[entry.field];
    if (typeof current !== "string") return;
    onChange(entry.field, removeMentionToken(current, entry.slug));
  };

  const removeReference = (entry: ReferenceChipEntry) => {
    if (entry.index === null) {
      onChange(entry.field, undefined);
      return;
    }
    const raw = params[entry.field];
    if (!Array.isArray(raw)) return;
    const next = raw.filter((_, i) => i !== entry.index);
    onChange(entry.field, next.length ? next : undefined);
  };

  return (
    <div data-testid="composer-chips" className="flex max-h-64 w-full flex-wrap items-start gap-2 overflow-y-auto overscroll-contain">
      {mentionEntries.map((entry) => {
        const asset = elementBySlug.get(entry.slug);
        if (!asset) return null;
        const fieldLabel = MENTION_FIELD_LABELS[entry.field];
        const ids = asset.canonical_reference_ids;
        return (
          <span key={`mention-${entry.field}-${entry.slug}`} className={CARD}>
            <span className="flex max-w-full flex-wrap gap-1">
              {ids.length ? ids.map((id, index) => {
                const resolved = asset.canonical_references?.find((item) => item.asset_id === id);
                const label = ids.length > 1 ? `${asset.name} ${index + 1}` : asset.name;
                return (
                  <ReferencePreviewButton
                    key={id}
                    assetId={id}
                    storageKey={resolved?.storage_key}
                    thumbnailKey={resolved?.thumbnail_storage_key}
                    label={label}
                    className="h-20 w-24"
                    onPreview={() => setPreview({ assetId: id, label })}
                  />
                );
              }) : (
                <span aria-hidden className="flex h-20 w-24 items-center justify-center rounded-lg bg-studio-card text-sm font-semibold uppercase text-studio-accent">
                  {asset.name.slice(0, 1)}
                </span>
              )}
            </span>
            <span className="max-w-[12rem] truncate pl-1 pr-7 font-medium text-studio-ink">
              {asset.name}
              {entry.field !== "prompt" && <span className="text-studio-muted"> · {fieldLabel}</span>}
            </span>
            <button
              type="button"
              onClick={() => removeMention(entry)}
              aria-label={`Remove @${entry.slug} mention from ${fieldLabel}`}
              className={REMOVE}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}
      {references.map((entry) => {
        const label = `${referenceFieldLabel(entry.field)}${entry.index === null ? "" : ` ${entry.index + 1}`}`;
        const type = referenceType(entry.reference);
        return (
          <span key={`ref-${entry.field}-${entry.index ?? "single"}-${entry.reference.asset_id}`} className={CARD}>
            <ReferencePreviewButton
              assetId={entry.reference.asset_id}
              storageKey={entry.reference.storage_key}
              thumbnailKey={entry.reference.thumbnail_storage_key}
              label={label}
              type={type}
              className="h-20 w-28"
              onPreview={() => setPreview({ assetId: entry.reference.asset_id, storageKey: entry.reference.storage_key, label, type })}
            />
            <span className="px-1 text-studio-muted">{label}</span>
            <button
              type="button"
              onClick={() => removeReference(entry)}
              aria-label={`Remove ${referenceFieldLabel(entry.field)} reference`}
              className={REMOVE}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}
      {preview && <ReferenceLightbox target={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
